import { useLiveQuery } from "dexie-react-hooks";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { useOnline } from "@/hooks/use-online";
import { db } from "@/lib/db";
import { cn } from "@/lib/utils";
import { useUIStore } from "@/stores/ui";

type Status = "online" | "offline" | "syncing";

const styles: Record<Status, { dot: string; label: string }> = {
	online: { dot: "bg-emerald-500", label: "Online" },
	offline: { dot: "bg-muted-foreground", label: "Offline. Changes are saved on this device." },
	syncing: { dot: "bg-amber-500 animate-pulse", label: "Syncing" },
};

export function StatusDot() {
	const online = useOnline();
	const syncing = useUIStore((s) => s.syncing);
	const status: Status = !online ? "offline" : syncing ? "syncing" : "online";
	// Changes saved on this device but not yet acknowledged by the server.
	const pending = useLiveQuery(() => db.outbox.count(), []) ?? 0;
	const { dot } = styles[status];
	const waiting = pending ? ` ${pending} change${pending === 1 ? "" : "s"} waiting to sync.` : "";
	const label = `${styles[status].label}${status !== "offline" && pending ? "." : ""}${waiting}`;

	return (
		<Tooltip>
			<TooltipTrigger asChild>
				<span
					role="status"
					aria-label={label}
					data-status={status}
					tabIndex={0}
					className="inline-flex size-11 items-center justify-center rounded-md md:size-9"
				>
					<span className={cn("size-2.5 rounded-full", dot)} />
				</span>
			</TooltipTrigger>
			<TooltipContent>{label}</TooltipContent>
		</Tooltip>
	);
}
